import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bus,
  Calendar,
  Clock,
  MapPin,
  Truck,
  UserCheck,
  Briefcase,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { fetchBusTastings } from "../lib/api";

function fmtTime(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
}

function relativeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00");
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86400000);
  const dateStr = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (days === 0) return { label: `${dateStr} · TODAY`, tone: "due" };
  if (days === 1) return { label: `${dateStr} · TOMORROW`, tone: "due" };
  if (days < 0) return { label: `${dateStr} · ${Math.abs(days)}d ago`, tone: "overdue" };
  if (days < 7) return { label: `${dateStr} · in ${days}d`, tone: "soon" };
  return { label: dateStr, tone: "future" };
}

/**
 * Dashboard card showing the next 3 scheduled bus tastings + a callout for
 * any unscheduled requests. Lets every team member see "what's coming up"
 * at a glance from the home page.
 */
export function UpcomingTastingsCard({ limit = 3 }) {
  const [scheduled, setScheduled] = useState(null);
  const [requestedCount, setRequestedCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [sched, req] = await Promise.all([
          fetchBusTastings({ status: "scheduled" }),
          fetchBusTastings({ status: "requested" }),
        ]);
        setScheduled(sched);
        setRequestedCount(req.length);
      } catch {
        setScheduled([]);
      }
    })();
  }, []);

  const next = (scheduled || [])
    .slice()
    .sort((a, b) => {
      const ad = a.scheduled_date || "9999-12-31";
      const bd = b.scheduled_date || "9999-12-31";
      return ad.localeCompare(bd) || (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
    })
    .slice(0, limit);

  return (
    <div data-testid="upcoming-tastings-card" className="panel p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono">
          <Bus className="w-3 h-3 text-emerald-300" />
          Upcoming bus events
        </div>
        <Link
          to="/bus-tastings"
          data-testid="upcoming-tastings-see-all"
          className="text-[10px] font-mono text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1"
        >
          See all
          <ArrowRight className="w-2.5 h-2.5" />
        </Link>
      </div>

      {requestedCount > 0 && (
        <Link
          to="/bus-tastings"
          data-testid="upcoming-tastings-requested-banner"
          className="block mb-3 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15 transition-colors"
        >
          <div className="flex items-center gap-2 text-xs">
            <AlertCircle className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-amber-100 font-medium">
              {requestedCount} pending request{requestedCount === 1 ? "" : "s"}
            </span>
            <span className="text-amber-300/80">needs scheduling →</span>
          </div>
        </Link>
      )}

      {scheduled === null ? (
        <div className="text-xs text-zinc-500 py-6 text-center">Loading…</div>
      ) : next.length === 0 ? (
        <div className="text-xs text-zinc-500 py-8 text-center">
          <Bus className="w-5 h-5 mx-auto text-zinc-700 mb-2" />
          No bus events on the calendar.
          <div className="mt-1 text-[10px] text-zinc-600">
            Sales reps can request from any dispensary detail page.
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {next.map((t) => {
            const rel = relativeDate(t.scheduled_date);
            const crew = [
              t.driver_id ? { id: `driver-${t.driver_id}`, label: "DRIVER", icon: Truck, name: t.crew_names?.[t.driver_id], color: "text-sky-300" } : null,
              ...(t.host_ids || []).map((id) =>
                t.crew_names?.[id]
                  ? { id: `host-${id}`, label: "HOST", icon: UserCheck, name: t.crew_names[id], color: "text-fuchsia-300" }
                  : null
              ),
              ...(t.lead_host_ids || []).map((id) =>
                t.crew_names?.[id]
                  ? { id: `mgr-${id}`, label: "MGR", icon: Briefcase, name: t.crew_names[id], color: "text-amber-300" }
                  : null
              ),
            ].filter(Boolean);
            return (
              <li key={t.id}>
                <Link
                  to={`/dispensaries/${t.dispensary_id}`}
                  data-testid={`upcoming-tasting-${t.id}`}
                  className="block px-3 py-2.5 rounded-md border border-zinc-800 hover:border-emerald-500/30 hover:bg-zinc-900/40 transition-colors space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-zinc-50 font-medium truncate">
                      {t.dispensary_name}
                    </div>
                    {rel && (
                      <span
                        className={`text-[10px] font-mono font-semibold whitespace-nowrap ${
                          rel.tone === "overdue"
                            ? "text-rose-300"
                            : rel.tone === "due"
                            ? "text-amber-300"
                            : rel.tone === "soon"
                            ? "text-emerald-300"
                            : "text-zinc-400"
                        }`}
                      >
                        {rel.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[10px] text-zinc-500 font-mono">
                    {t.dispensary_city && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5" />
                        {t.dispensary_city}
                      </span>
                    )}
                    {t.scheduled_time && (
                      <span className="inline-flex items-center gap-1 text-zinc-300">
                        <Clock className="w-2.5 h-2.5" />
                        {fmtTime(t.scheduled_time)}
                      </span>
                    )}
                    {crew.length === 0 && (
                      <span className="text-rose-300/80">no crew yet</span>
                    )}
                  </div>
                  {crew.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {crew.map((c) => {
                        const Icon = c.icon;
                        return (
                          <span
                            key={c.id}
                            className={`inline-flex items-center gap-0.5 text-[9px] font-mono ${c.color}`}
                          >
                            <Icon className="w-2.5 h-2.5" />
                            {c.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default UpcomingTastingsCard;
